/**
 * Message prioritization utilities for BizChat iframe communication.
 *
 * This module provides helpers to classify and defer low-priority BizChat messages
 * to reduce main thread blocking. Low-priority messages (state changes, heartbeats)
 * are processed during idle time using requestIdleCallback, while user-initiated
 * actions are processed immediately to maintain responsiveness.
 */

/**
 * Determines if a BizChat message function should be treated as low-priority.
 * Low-priority messages are deferred to idle time to avoid blocking the main thread.
 *
 * @param func The message function name from BizChat
 * @returns true if the message should be deferred, false if it should be processed immediately
 */
export function isLowPriorityMessage(func: string): boolean {
    // Messages that are explicitly low-priority and can be deferred
    // These are background updates, state changes, and non-user-initiated events.
    // Match keys exactly against the names registered via setBizChatHandlers
    // (see owa-bizchat-context-handlers/registerContextHandlers and the defaults
    // in bizChatHandlers.ts).
    const lowPriorityFuncs: ReadonlySet<string> = new Set([
        "QuerySubmit", // Telemetry-only (BizchatSidepane_BizchatStatus_Milestone)
        "onUserSubmittedQuery", // Telemetry-only (BizchatSidepane_BizchatStatus_Milestone)
        "queryResponseReceived", // Response streaming (not user-initiated)
        "firstTokenReceived", // First token in streaming response
        "referenceListGeneratingForOLK", // Background reference generation
        "referenceListPreparedForOLK", // Background reference preparation complete
        "prefetchContextRequested", // Background prefetch
        "calendarEventChangedSignalRequested", // Background signal, not user-initiated
        "ClientQueryError", // Background error report
        "SydneyClientUnavailableError", // Background error report
        "ReceivedResponseFromSydneyError", // Background error report
        "BlankScreenErrorQuerySubmit", // Background error report
        "AbortOnErrorInLocalPlugin", // Error path, not user-initiated
        "AbortByUser", // User aborted operation, for telemetry purposes
        "EscapeHatchAbortByUser", // User aborted via escape hatch, for telemetry purposes
    ]);
    
    // High-priority messages that must be processed immediately.
    // These are user-initiated actions and critical interactions.
    const highPriorityFuncs: ReadonlySet<string> = new Set([
        "draftAssistantActionRequested", // User requested draft assistant action
        "onClickHandoffActionButton", // User clicked handoff button
        "selectTimeSlots", // User selected time slots
        "highlightEvents", // User-initiated highlight
        "calendarAgentModeActionRequested", // User requested calendar agent action
        "openDirectivesSettings", // User opened settings
        "filterEvents", // User filtered events
        "ChatReady", // Chat initialization
        "appLoadedFromCache", // Cache-resume initialization (mirrors ChatReady setup)
        "onAgentModeSelectionChanged", // Agent mode selection
        "onConversationIdChanged", // Conversation state change
        "clearHighlightedEvents", // Cleanup operation
        "enableCopilotHighlighting", // UI state change
    ]);
    
    // High-priority wins if a func appears in both lists.
    if (highPriorityFuncs.has(func)) {
        return false;
    }
    
    if (lowPriorityFuncs.has(func)) {
        return true;
    }
    
    // Default to high priority (immediate processing) for unknown messages.
    return false;
}

/**
 * Defers execution of a message processing function to idle time.
 * Uses requestIdleCallback to schedule work when the browser is idle,
 * with a timeout fallback to ensure messages are eventually processed.
 * Falls back to setTimeout in browsers that don't support requestIdleCallback (e.g., Safari).
 *
 * @param processFn The async function to execute during idle time
 * @param timeout Fallback timeout in milliseconds (default: 100ms)
 * @returns A promise that resolves with the result of processFn
 */
export function deferMessageProcessing<T>(processFn: () => Promise<T>, timeout: number = 100): Promise<T> {
    return new Promise((resolve, reject) => {
        const callback = async () => {
            try {
                const result = await processFn();
                resolve(result);
            }
            catch (e) {
                reject(e);
            }
        };
        
        // Safari and some other browsers don't support requestIdleCallback
        if ((window as any).requestIdleCallback) {
            (window as any).requestIdleCallback(callback, { timeout });
        }
        else {
            // Fallback to setTimeout with the same timeout
            setTimeout(callback, timeout);
        }
    });
}
